import { AppStore } from 'src/store';
import { RouteConfig, NavigationGuardNext } from 'vue-router';
import { Permissions, DealerRoles } from 'src/constants/ClaimTypes';

const RouteNames = {
  Login: 'Login',
  ForgotPassword: 'Forgot Password',
  ResetPassword: 'Reset Password',
  ConfirmEmail: 'Confirm Email',
  AcceptTermsConditions: 'Accept Terms and Conditions',
  DealerAdminAccountRegistration: 'Dealer Admin Account Registration',
  Dashboard: 'Dashboard',
  ManageUsers: 'Manage Users',
  ManageRoles: 'Manage Roles',
  ManageEmails: 'Manage Emails',
  ChangePassword: 'Change Password',
  EditProfile: 'Edit Profile',
  PdfViewer: 'PdfViewer',
  WarrantyRegistrations: 'Warranty Registrations',
  RegisterVehicle: 'Register Vehicle',
  WarrantyRecalls: 'Warranty Recalls',
  WarrantyClaims: 'Warranty Claims',
  WarrantyStatusClaims: 'Warranty Status Claims',
  SalesStatusOrder: 'Sales Status Order',
  VinStatusType: 'Vin Status Type',
  ProductItems: 'Product Items',
  ShoppingCart: 'Shopping Cart',
  CheckoutOrderInfo: 'Checkout Order Info',
  CheckoutReview: 'Checkout Review',
  Invoices: 'Invoices',
  W9NewRequest: 'W9 New Request',
  W9Requests: 'W9 Requests',
  SpiffRequests: 'Spiff Requests',
  SpiffRequestForm: 'Spiff Request Form',
  SalesOrders: 'Sales Orders',
  ClaimDetail: 'Claim Detail',
  MarketingMaterials: 'Marketing Materials',
  RvFaqs: 'Rv Faqs',
  InfoVideos: 'Info Videos'
};

const routes: RouteConfig[] = [
  {
    path: '/login',
    name: RouteNames.Login,
    component: () => import('pages/Login.vue')
  },
  {
    path: '/forgot-password',
    name: RouteNames.ForgotPassword,
    component: () => import('pages/account/ForgotPassword.vue')
  },
  {
    path: '/reset-password',
    name: RouteNames.ResetPassword,
    component: () => import('pages/account/ResetPassword.vue')
  },
  {
    path: '/confirm-email',
    name: RouteNames.ConfirmEmail,
    component: () => import('pages/account/ConfirmEmail.vue')
  },
  {
    path: '/register-admin',
    name: RouteNames.DealerAdminAccountRegistration,
    component: () => import('src/pages/account/DealerAdminAccountRegistration.vue')
  },
  {
    path: '/',
    component: () => import('layouts/MainLayout.vue'),
    children: [
      { path: '', name: RouteNames.Dashboard, component: () => import('src/pages/Dashboard.vue') },
      {
        path: '/admin/manage-users',
        name: RouteNames.ManageUsers,
        component: () => import('pages/admin/ManageUsers.vue'),
        beforeEnter(to, from, next) {
          HasPermission(RouteNames.ManageUsers) ? next() : next({ name: RouteNames.Dashboard });
        }
      },
      {
        path: '/admin/manage-roles',
        name: RouteNames.ManageRoles,
        component: () => import('pages/admin/ManageRoles.vue'),
        beforeEnter(to, from, next) {
          HasPermission(RouteNames.ManageRoles) ? next() : next({ name: RouteNames.Dashboard });
        }
      },
      {
        path: '/admin/manage-emails',
        name: RouteNames.ManageEmails,
        component: () => import('pages/admin/ManageEmails.vue'),
        beforeEnter(to, from, next) {
          HasPermission(RouteNames.ManageEmails) ? next() : next({ name: RouteNames.Dashboard });
        }
      },
      {
        path: '/account/change-password',
        name: RouteNames.ChangePassword,
        component: () => import('pages/account/ChangePassword.vue')
      },
      {
        path: '/account/edit-profile',
        name: RouteNames.EditProfile,
        component: () => import('pages/account/EditProfile.vue')
      },
      { path: '/invoices', name: RouteNames.Invoices, component: () => import('pages/Invoices.vue') },
      {
        path: '/pdfviewer',
        name: RouteNames.PdfViewer,
        component: () => import('src/pages/PdfViewer.vue'),
        props: true
      },
      { path: '/product-items', name: RouteNames.ProductItems, component: () => import('src/pages/Products.vue') },
      {
        path: '/shopping-cart',
        name: RouteNames.ShoppingCart,
        component: () => import('src/pages/cart/ShoppingCart.vue'),
        beforeEnter(to, from, next) {
          canUseShoppingCart(next);
        }
      },
      {
        path: '/warranty-registrations',
        name: RouteNames.WarrantyRegistrations,
        component: () => import('src/pages/warranty/WarrantyRegistrations.vue')
      },
      {
        path: '/warranty-registrations/:filter',
        name: RouteNames.VinStatusType,
        component: () => import('src/pages/warranty/WarrantyRegistrations.vue'),
        props: true
      },
      {
        path: '/warranty-registrations/:vin/register',
        name: RouteNames.RegisterVehicle,
        component: () => import('src/pages/warranty/RegisterVehicle.vue'),
        props: true
      },
      {
        path: '/warranty-recalls',
        name: RouteNames.WarrantyRecalls,
        component: () => import('src/pages/warranty/WarrantyRecalls.vue')
      },
      {
        path: '/warranty-claims',
        name: RouteNames.WarrantyClaims,
        component: () => import('src/pages/warranty/WarrantyClaims.vue')
      },
      {
        path: '/warranty-claims/:aging/:presetStatusId',
        name: RouteNames.WarrantyStatusClaims,
        component: () => import('src/pages/warranty/WarrantyClaims.vue'),
        props: true
      },
      {
        path: '/sales-orders/:filter',
        name: RouteNames.SalesStatusOrder,
        component: () => import('src/pages/SalesOrders.vue'),
        props: true
      },
      {
        path: '/warranty-claims/:claimNumber',
        name: RouteNames.ClaimDetail,
        component: () => import('src/pages/warranty/ClaimDetail.vue'),
        props: true
      },
      { path: '/product-items', name: RouteNames.ProductItems, component: () => import('src/pages/Products.vue') },
      {
        path: '/shopping-cart',
        name: RouteNames.ShoppingCart,
        component: () => import('src/pages/cart/ShoppingCart.vue'),
        beforeEnter(to, from, next) {
          canUseShoppingCart(next);
        }
      },
      {
        path: '/cart-checkout/order-information',
        name: RouteNames.CheckoutOrderInfo,
        component: () => import('src/pages/cart/CheckoutOrderInfo.vue'),
        beforeEnter(to, from, next) {
          canUseShoppingCart(next);
        }
      },
      {
        path: '/cart-checkout/order-review',
        name: RouteNames.CheckoutReview,
        component: () => import('src/pages/cart/CheckoutReview.vue'),
        beforeEnter(to, from, next) {
          canPlaceOrder(next);
        }
      },
      {
        path: '/w9-new-request',
        name: RouteNames.W9NewRequest,
        component: () => import('src/pages/w9/W9NewRequest.vue'),
        props: true,
        beforeEnter(to, from, next) {
          HasPermission(RouteNames.W9NewRequest) ? next() : next({ name: RouteNames.Dashboard });
        }
      },
      {
        path: '/w9-requests',
        name: RouteNames.W9Requests,
        component: () => import('src/pages/w9/W9Requests.vue'),
        beforeEnter(to, from, next) {
          HasPermission(RouteNames.W9Requests) ? next() : next({ name: RouteNames.Dashboard });
        }
      },
      {
        path: '/spiff-requests',
        name: RouteNames.SpiffRequests,
        component: () => import('src/pages/spiff/SpiffRequest.vue'),
        beforeEnter(to, from, next) {
          HasPermission(RouteNames.SpiffRequests) ? next() : next({ name: RouteNames.Dashboard });
        }
      },
      {
        path: '/spiff-request-form/:vin/:spiffId/:type',
        name: RouteNames.SpiffRequestForm,
        component: () => import('src/pages/spiff/SpiffRequestForm.vue'),
        props: true,
        beforeEnter(to, from, next) {
          HasPermission(RouteNames.SpiffRequestForm) ? next() : next({ name: RouteNames.Dashboard });
        }
      },
      {
        path: '/sales-orders',
        name: RouteNames.SalesOrders,
        component: () => import('src/pages/SalesOrders.vue'),
        props: true
      },
      {
        path: '/marketing-materials',
        name: RouteNames.MarketingMaterials,
        component: () => import('src/pages/MarketingMaterials.vue')
      },
      {
        path: '/customer-resources/faq',
        name: RouteNames.RvFaqs,
        component: () => import('src/pages/customerResources/RvFaq.vue')
      },
      {
        path: '/customer-resources/informational-videos',
        name: RouteNames.InfoVideos,
        component: () => import('src/pages/customerResources/InformationalVideos.vue')
      }
    ]
  },

  // Always leave this as last one,
  // but you can also remove it
  {
    path: '*',
    component: () => import('pages/Error404.vue')
  }
];

function canUseShoppingCart(next: NavigationGuardNext<Vue>) {
  if (!HasPermission(RouteNames.ShoppingCart)) {
    next('/product-items');
  } else {
    next();
  }
}

function canPlaceOrder(next: NavigationGuardNext<Vue>) {
  if (!HasPermission(RouteNames.CheckoutReview) || !AppStore.state.orderInfo.dealerLocation) {
    next('/cart-checkout/order-information');
  } else {
    next();
  }
}

function HasPermission(routeName: string): boolean {
  const routePermissions = RoutePermissions(AppStore.state.user.dealerRoles);
  return routePermissions && routePermissions.includes(routeName);
}

function RoutePermissions(dealerRoles: Array<string>): Array<string> {
  let routePermissions: Array<string> = DealerRoleRoutes.AuthorizedPublicRoutes;

  if (AppStore.state.user.hasFullRights) {
    return routePermissions.concat(DealerRoleRoutes.FullPermission);
  }

  if (dealerRoles.some(x => x == DealerRoles.DealerAdmin)) {
    routePermissions = routePermissions.concat(DealerRoleRoutes.DealerAdmin);
  } else if (dealerRoles.some(x => x == DealerRoles.DealerFinance)) {
    routePermissions = routePermissions.concat(DealerRoleRoutes.DealerFinance);
  } else if (dealerRoles.some(x => x == DealerRoles.DealerWarranty)) {
    routePermissions = routePermissions.concat(DealerRoleRoutes.DealerWarranty);
  }

  if (AppStore.state.user.permissions.includes(Permissions.ViewDealerUsers)) {
    routePermissions = [RouteNames.ManageUsers];
  }

  if (AppStore.state.user.permissions.includes(Permissions.ManageRoles)) {
    routePermissions = [RouteNames.ManageRoles];
  }
  return routePermissions;
}

export class DealerRoleRoutes {
  static DealerAdmin = [
    RouteNames.ManageUsers,
    RouteNames.W9Requests,
    RouteNames.W9NewRequest,
    RouteNames.RegisterVehicle,
    RouteNames.SpiffRequests,
    RouteNames.SpiffRequestForm
  ];

  static DealerFinance = [RouteNames.W9Requests, RouteNames.SpiffRequests];

  static DealerWarranty = [
    RouteNames.ShoppingCart,
    RouteNames.CheckoutOrderInfo,
    RouteNames.CheckoutReview,
    RouteNames.RegisterVehicle
  ];

  static FullPermission = [
    RouteNames.ManageUsers,
    RouteNames.ShoppingCart,
    RouteNames.CheckoutOrderInfo,
    RouteNames.RegisterVehicle,
    RouteNames.CheckoutReview,
    RouteNames.W9Requests,
    RouteNames.W9NewRequest,
    RouteNames.SpiffRequests,
    RouteNames.SpiffRequestForm
  ];

  static AuthorizedPublicRoutes = [
    RouteNames.Dashboard,
    RouteNames.WarrantyRegistrations,
    RouteNames.WarrantyRecalls,
    RouteNames.WarrantyClaims,
    RouteNames.ProductItems,
    RouteNames.Invoices,
    RouteNames.MarketingMaterials,
    RouteNames.RvFaqs,
    RouteNames.InfoVideos,
    RouteNames.SalesOrders
  ];
}

export { routes, RouteNames, RoutePermissions, HasPermission };
